import { formatCentsForDisplay as formatPrice } from '@/utils/money';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  CircularProgress,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useOpenConsolidatedInvoices } from '@/hooks/useConsolidatedInvoices';
import { formatDate, formatDateTime } from '@/utils/locale';

interface OpenConsolidatedInvoicesProps {
  search: string;
}

export function OpenConsolidatedInvoices({ search }: OpenConsolidatedInvoicesProps) {
  const { t } = useTranslation('invoices');
  const query = useOpenConsolidatedInvoices();
  const needle = search.trim().toLowerCase();
  const invoices = (query.data ?? []).filter((invoice) => {
    if (needle === '') return true;
    return (
      invoice.companyName.toLowerCase().includes(needle)
      || invoice.invoiceNumber.toLowerCase().includes(needle)
    );
  });

  if (query.isPending) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (query.isError) {
    return (
      <Alert severity="error" data-testid="open-consolidated-error">
        {t('consolidation.loadError')}
      </Alert>
    );
  }

  return (
    <Box data-testid="open-consolidated-invoices">
      <Typography variant="h6" sx={{ mb: 1 }}>
        {t('consolidation.openTitle')}
      </Typography>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('columns.invoiceNumber')}</TableCell>
              <TableCell>{t('consolidation.openCompany')}</TableCell>
              <TableCell align="right">{t('consolidation.openWorkOrders')}</TableCell>
              <TableCell align="right">{t('consolidation.openTotal')}</TableCell>
              <TableCell>{t('consolidation.openPeriodStart')}</TableCell>
              <TableCell>{t('consolidation.openNextCutoff')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {invoices.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ color: 'text.secondary' }}>
                  {t('consolidation.openEmpty')}
                </TableCell>
              </TableRow>
            ) : (
              invoices.map((invoice) => (
                <TableRow key={invoice.id} data-testid={`open-consolidated-${invoice.id}`}>
                  <TableCell>
                    <Link component={RouterLink} to={`/invoices/${invoice.id}`}>
                      {invoice.invoiceNumber}
                    </Link>
                  </TableCell>
                  <TableCell>{invoice.companyName}</TableCell>
                  <TableCell align="right" data-testid="open-consolidated-count">
                    {invoice.workOrderCount}
                  </TableCell>
                  <TableCell align="right" data-testid="open-consolidated-total">
                    {formatPrice(invoice.totalCents)}
                  </TableCell>
                  <TableCell>{formatDate(invoice.periodStart)}</TableCell>
                  <TableCell>{formatDateTime(invoice.nextCutoffAt)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
}
